import { zodResolver } from '@hookform/resolvers/zod'
import { Check, X } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import z from 'zod'

import { FormError } from '@/app/components/ui/FormError'
import { Button } from '@/app/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog'
import { DiscreteLoader } from '@/app/components/ui/discrete-loader'
import { Input } from '@/app/components/ui/input'
import { posCustomerService, CreateCustomerPayload } from '@/app/services/pos.service'

const formSchema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio').max(255, 'Demasiado largo'),
  phone: z.string().min(10, 'El teléfono debe tener al menos 10 dígitos').max(30, 'Demasiado largo'),
  email: z.union([z.string().email('Formato de correo inválido'), z.literal('')]).optional(),
  notes: z.string().optional(),
  is_active: z.boolean(),
})

type FormValidate = z.infer<typeof formSchema>

export interface NewCustomerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  setShowNewCustomerModal: (show: boolean) => void
  onSuccess?: () => void
}

export function NewCustomerModal({
  open,
  onOpenChange,
  setShowNewCustomerModal,
  onSuccess
}: NewCustomerModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValidate>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: { name: '', phone: '', email: '', notes: '', is_active: true },
  })

  const onSubmit = async (formData: FormValidate) => {
    try {
      // Si el email esta vacio, lo mandamos como undefined para que el backend lo acepte como null
      const payload: CreateCustomerPayload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email === '' ? undefined : formData.email,
        notes: formData.notes,
        is_active: formData.is_active,
      } as CreateCustomerPayload

      await posCustomerService.createCustomer(payload)
      toast.success('Cliente registrado exitosamente')
      setShowNewCustomerModal(false)
      reset()

      if (onSuccess) {
        onSuccess()
      }
    } catch (caught: unknown) {
      const response = caught as {
        response?: { data?: { message?: string; errors?: Record<string, string[]> } }
      }
      const serverErrors = response.response?.data?.errors
      const phoneMessage = serverErrors?.phone?.[0]
      const emailMessage = serverErrors?.email?.[0]

      if (phoneMessage) setError('phone', { type: 'server', message: phoneMessage })
      if (emailMessage) setError('email', { type: 'server', message: emailMessage })

      toast.error(
        phoneMessage
        || emailMessage
        || Object.values(serverErrors ?? {}).flat().find(Boolean)
        || response.response?.data?.message
        || 'Error al registrar el cliente',
      )
    }
  }

  // Se ejecuta cuando se cierra el modal para limpiar el form
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      reset()
    }
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Registro Rápido de Cliente</DialogTitle>
          <DialogDescription className="sr-only">
            Formulario rápido para registrar un nuevo cliente desde el punto de venta
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-4">
          <div className="rounded-lg border border-primary/20 bg-card p-3">
            <p className="text-sm text-card-foreground">
              Registra un nuevo cliente para poder asignarle puntos y llevar un historial de compras.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-card-foreground">
              Nombre Completo <span className="text-red-600">*</span>
            </label>
            <Input {...register('name')} placeholder="Ej: Juan Pérez García" />
            {errors.name && <FormError message={errors.name.message} />}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-card-foreground">
                Teléfono <span className="text-red-600">*</span>
              </label>
              <Input {...register('phone')} placeholder="555-1234567" />
              {errors.phone && <FormError message={errors.phone.message} />}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-card-foreground">
                Email
              </label>
              <Input {...register('email')} type="email" placeholder="correo@ejemplo.com" />
              {errors.email && <FormError message={errors.email.message} />}
            </div>
          </div>

          {/* 
          // Descomentar despues de cambios de backend
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="mb-2 text-xs text-slate-600">
              Dirección (Opcional)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Calle" className="h-8 text-sm" />
              <Input placeholder="Número" className="h-8 text-sm" />
              <Input placeholder="Colonia" className="h-8 text-sm" />
              <Input placeholder="Código Postal" className="h-8 text-sm" />
            </div>
          </div>
          */}

          <div className="space-y-2">
            <label className="text-sm font-medium text-card-foreground">
              Comentarios
            </label>
            <Input {...register('notes')} placeholder="Notas adicionales..." />
            {errors.notes && <FormError message={errors.notes.message} />}
          </div>

          <div className="flex gap-3 border-t border-border pt-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <DiscreteLoader />
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Guardar y Seleccionar
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={() => {
                setShowNewCustomerModal(false)
                reset()
              }}
              className="flex-1"
            >
              <X className="mr-2 h-4 w-4" />
              Cancelar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
